import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs';
import { IconModule } from 'src/app/shared/icon/icon.module';
import {
  AdminSessionApiService,
  type PdSession,
  type PdSessionAiResponseFace,
  type PdSessionAiResponsePan,
} from 'src/app/core/services/admin-session-api.service';

interface ComparisonRow {
  label: string;
  /**
   * DocumentType.requirement_key ('pan', 'selfie', 'aadhaar-back', …).
   * Intentionally an open string: a new country's document type must not
   * require a frontend change.
   */
  slot: string;
  captured: string | null;
  uploaded: string | null;
  matched: boolean | null;
  /** Heading for the reference column — the two sources are not equivalent. */
  uploadedLabel: string;
}

@Component({
  selector: 'app-kyc-capture-comparison',
  standalone: true,
  imports: [CommonModule, IconModule],
  templateUrl: './kyc-capture-comparison.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KycCaptureComparisonComponent implements OnChanges {
  @Input() sessionId?: number | null;
  @Input() tenantId?: number | null;
  @Input() pan?: PdSessionAiResponsePan | null;
  @Input() face?: PdSessionAiResponseFace | null;
  @Output() sessionUpdated = new EventEmitter<PdSession>();

  private readonly api = inject(AdminSessionApiService);
  private readonly cdr = inject(ChangeDetectorRef);

  recompareLoading: string | null = null;
  recompareErrorByLabel: Record<string, string> = {};
  // Super-admin simulate sessions store only the captured image; the uploaded reference
  // is resolved lazily from the agent's reference doc via the backend.
  private resolvedUploaded: { pan: string | null; face: string | null } = { pan: null, face: null };
  private resolvedSource: Record<string, string | undefined> = {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sessionId'] || changes['tenantId'] || changes['pan'] || changes['face']) {
      this.resolvedUploaded = { pan: null, face: null };
      this.resolvedSource = {
        face: this.face?.uploaded_source ?? undefined,
      };
      this.fetchReferenceIfMissing('pan', this.pan?.uploaded_image ?? null);
      this.fetchReferenceIfMissing('face', this.face?.uploaded_image ?? null);
    }
  }

  private fetchReferenceIfMissing(slot: string, inlineUploaded: string | null): void {
    if (inlineUploaded || !this.sessionId) return;
    this.api.getKycReferenceImage(this.sessionId, slot, this.tenantId).subscribe({
      next: (res) => {
        this.resolvedUploaded = { ...this.resolvedUploaded, [slot]: res?.data?.image ?? null };
        this.resolvedSource = { ...this.resolvedSource, [slot]: res?.data?.source };
        this.cdr.markForCheck();
      },
      error: () => this.cdr.markForCheck(),
    });
  }

  get rows(): ComparisonRow[] {
    const all: ComparisonRow[] = [
      {
        label: 'PAN Card',
        slot: 'pan',
        captured: this.pan?.captured_image ?? null,
        uploaded: this.pan?.uploaded_image ?? this.resolvedUploaded.pan,
        matched: this.pan?.matched ?? null,
        uploadedLabel: this.referenceLabel('pan'),
      },
      {
        label: 'Selfie',
        slot: 'face',
        captured: this.face?.captured_image ?? null,
        uploaded: this.face?.uploaded_image ?? this.resolvedUploaded.face,
        matched: this.face?.matched ?? null,
        uploadedLabel: this.referenceLabel('face'),
      },
    ];
    return all.filter((r) => r.captured != null || r.uploaded != null);
  }

  /** UIDAI photo and an uploaded document are different kinds of evidence — name which. */
  private referenceLabel(slot: string): string {
    return this.resolvedSource[slot] === 'aadhaar_ekyc' ? 'Aadhaar e-KYC photo' : 'Uploaded';
  }

  rowStatus(row: ComparisonRow): string {
    if (row.matched === null) return '';
    return row.matched ? '✓ Matched' : '✗ No match';
  }

  rowStatusClass(row: ComparisonRow): string {
    return row.matched ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  }

  // Both images are required for a fresh comparison; hide once already matched.
  canRecompare(row: ComparisonRow): boolean {
    return !!(this.sessionId && row.captured && row.uploaded && row.matched !== true);
  }

  isRecompareLoading(row: ComparisonRow): boolean {
    return this.recompareLoading === row.slot;
  }

  recompareError(row: ComparisonRow): string {
    return this.recompareErrorByLabel[row.label] ?? '';
  }

  compareAgain(row: ComparisonRow): void {
    const id = this.sessionId;
    if (!id || this.recompareLoading || !this.canRecompare(row)) return;

    this.recompareLoading = row.slot;
    this.recompareErrorByLabel = { ...this.recompareErrorByLabel, [row.label]: '' };

    this.api
      .recompareSession(id, row.slot, this.tenantId)
      .pipe(
        finalize(() => {
          this.recompareLoading = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          const session = res?.data;
          if (!session) {
            this.recompareErrorByLabel = {
              ...this.recompareErrorByLabel,
              [row.label]: 'Could not refresh the session after comparison.',
            };
            return;
          }
          if (row.slot === 'pan') this.pan = session.ai_response?.pan ?? this.pan;
          else this.face = session.ai_response?.face ?? this.face;
          this.sessionUpdated.emit(session);
        },
        error: (err) => {
          this.recompareErrorByLabel = {
            ...this.recompareErrorByLabel,
            [row.label]: err?.error?.message ?? err?.message ?? 'Comparison failed. Please try again.',
          };
        },
      });
  }
}
